import { createStore } from "vuex";
import APIAdapter from "./APIAdapter";
import moment from "moment";
import LocaleUtil from "../libs/LocaleUtil";
import { APP_LOCALES } from "../libs/LanguageUtil";
import DateUtil from "../libs/DateUtil";
import BookingOffsetUtil from "../libs/BookingOffsetUtil";
import TransformUtil from "../libs/TransformUtil";
import cookies from "../libs/cookies";
import vuetify from "../plugins/vuetify";
import ServerClock from "../libs/ServerClock";
import PointSellLock from "../libs/PointSellLock";
import WallClock from "../libs/WallClock";
import OpeningHoursUtil from "../libs/OpeningHoursUtil";
import {
  isPointSellRentable,
  isPointSellRentDisabled,
} from "../libs/PointSellUtil";

// Validate required environment variables
if (!import.meta.env.VITE_APP_INSTANCE_ID) {
  console.error("[CONFIG ERROR] VITE_APP_INSTANCE_ID is not set. Configure it in Amplify Console or .env.development");
}
if (!import.meta.env.VITE_MOB_DEPLOYMENT_ID) {
  console.error("[CONFIG ERROR] VITE_MOB_DEPLOYMENT_ID is not set. Configure it in Amplify Console or .env.development");
}

// Vuetify 3's v-date-picker emits Date objects (Vuetify 2 emitted 'YYYY-MM-DD'
// strings). The check-in/check-out state and cookies keep the string invariant,
// so a Date payload is formatted in LOCAL time here; strings and null pass through.
function normalizeDatePayload(payload) {
  return payload instanceof Date ? moment(payload).format("YYYY-MM-DD") : payload;
}

// MOB-065 — a PointSell read back from an order can arrive without its `location` and
// `pointMeeting` connections. Everything that books derives from those two, so a thin
// branch object is completed here before it reaches state: from the loaded branch list
// first (authoritative), then from the branch already in state, and only then accepted
// as it came. Returns the incoming object untouched when nothing is missing.
export function completePointSell(incoming, state) {
  if (incoming == null) return incoming;
  if (incoming.location != null && incoming.pointMeeting != null) return incoming;

  const candidates = [
    (state.pointSells || []).find((ps) => ps != null && ps.id === incoming.id),
    state.pointSell != null && state.pointSell.id === incoming.id ? state.pointSell : null,
  ];

  const completed = { ...incoming };
  for (const source of candidates) {
    if (source == null) continue;
    if (completed.location == null && source.location != null) {
      completed.location = source.location;
    }
    if (completed.pointMeeting == null && source.pointMeeting != null) {
      completed.pointMeeting = source.pointMeeting;
    }
  }
  return completed;
}

// In-flight `loadOpenHoursExceptions` requests, by cache key. Not state: a promise is not
// serializable and nothing renders from it.
const openHoursExceptionRequests = new Map();

// MOB-094 — the status of the read of the default opening hours. A failed read is not "no
// default schedule": the first leaves the branch unknown and blocks the booking (doctrine C34),
// the second leaves a legacy branch unrestricted.
export const OPEN_HOURS_READ = Object.freeze({
  PENDING: "PENDING", // not read yet, or the first read is in flight
  LOADED: "LOADED",
  FAILED: "FAILED",
});

// The cache key of the exceptions of 1 branch in 1 window. The action, the getter and the
// pages that read the cache build the key with `OpeningHoursUtil.exceptionsKey` (MOB-440).
export const openHoursExceptionsKey = OpeningHoursUtil.exceptionsKey;

// The in-flight `ensureDefaultOpenHours` request. Not state, for the reason above.
let defaultOpenHoursRequest = null;

// The in-flight `loadSkillLevelClients` request. Every group or camp line of the cart asks for
// the list on creation, so N lines share 1 request (MOB-452). Not state, for the reason above.
let skillLevelClientsRequest = null;

const store = createStore({
  state: {
    // kontext klienta - configured via Amplify environment variables
    appInstance: import.meta.env.VITE_APP_INSTANCE_ID,
    mobDeploymentId: import.meta.env.VITE_MOB_DEPLOYMENT_ID && !isNaN(parseInt(import.meta.env.VITE_MOB_DEPLOYMENT_ID)) ? parseInt(import.meta.env.VITE_MOB_DEPLOYMENT_ID) : null,

    mobConfig: null,

    // Runtime state for current locale (can be changed by user during session)
    locale: "cs",

    locations: [],
    locationsLoaded: false,
    location: null,
    pointSells: [],
    // MOB-518 — the whole list of `loadPointSells`, rent-disabled branches included. Only the
    // e-mail and the note of the checkout and confirmation pages read it (a school group lands
    // on a rent-disabled branch too). Everything that books reads `pointSells`.
    contactPointSells: [],
    pointSellsLoaded: false,
    pointMeetings: [],
    pointSell: null,
    skillLevelClients: [],
    checkInDateSchool: moment().format("YYYY-MM-DD"), // vychozi hodnota je o 1 den navic
    checkInDateRent: moment().format("YYYY-MM-DD"),
    checkOutDateRent: moment().format("YYYY-MM-DD"),
    checkInDateMinRent: moment().format("YYYY-MM-DD"), // nejnizsi mozna hranice, od kdy lze rezerovat
    currentOrderr: null,
    currentOrderrExpired: false, // staticky, aby nad Date objektem nemusel byt observu. Propisuje se po refreshi obj. a pri identifikaci expicitni expirace
    // MOB-043: objednavka je vazana na pobocku, ktera je mezitim v
    // `mobConfig.rentDisabledPointSells` (vyrazena z online prodeje). Rozpracovanou (DRAFT)
    // objednavku nelze v takovem stavu tise dokoncit - App.vue nad timto priznakem zobrazi
    // dialog a klient zalozi novou objednavku.
    currentOrderrPointSellDisabled: false,
    expireAt: null, // datum a cas, kdy objednavka expiruje. Ulozeno takto staticky kvuli eliminaci problikavani pri nacitani currentOrderr
    currentPayment: null,
    rentCategories: [],
    schoolCategories: [],
    staticContentList: [],
    rentProductRequirements: [],
    lastVisitedSegment: null, // jaky segment byl naposledy navstiven RENT nebo COURSE - pro ucely navratu na home page
    removingItemId: null, // ID polozky kosiku, ktera se prave maze
    // PointSell opening hours (structured models)
    pointSellOpenHoursDefault: [], // all branches' DEFAULT schedules, loaded at config load
    pointSellOpenHoursDefaultStatus: OPEN_HOURS_READ.PENDING, // MOB-094, see OPEN_HOURS_READ
    pointSellOpenHoursExceptions: {}, // cache: "{pointSellMssId}#{from}#{to}" -> exception schedules[]
    // MOB-440 — the cache keys whose last read failed (key -> true). A failed read is not "no
    // exceptions": it is never cached, and the key stays marked until a read succeeds.
    pointSellOpenHoursExceptionsFailed: {},
  },

  // Getters to access externalized configuration from mobConfig
  getters: {
    // Icons & Branding
    defaultOpenHoursByPointSell: (state) => (pointSellMssId) => {
      return (
        state.pointSellOpenHoursDefault.find(
          (s) => s.pointSellMssId === pointSellMssId
        ) || null
      );
    },
    // MOB-094 — true only after a read failed and until a read succeeds. A pending read is not
    // a failure: the pages that need the hours wait for it under their loaders.
    defaultOpenHoursReadFailed: (state) =>
      state.pointSellOpenHoursDefaultStatus === OPEN_HOURS_READ.FAILED,
    // MOB-440 — true only after the read of the exceptions of a branch and window failed, and
    // until a read succeeds. A pending read is not a failure. The key is the one of the action
    // `loadOpenHoursExceptions`.
    openHoursExceptionsReadFailed: (state) => (pointSellMssId, windowFrom, windowTo) =>
      state.pointSellOpenHoursExceptionsFailed[
        openHoursExceptionsKey(pointSellMssId, windowFrom, windowTo)
      ] === true,
    mobRentIcon: state => state.mobConfig?.mobRentIcon || 'mdi-ski',
    mobSchoolIcon: state => state.mobConfig?.mobSchoolIcon || 'mdi-ski',

    // Localization
    locales: state => state.mobConfig?.locales || [...APP_LOCALES],
    defaultLocale: state => state.mobConfig?.defaultLocale || 'cs',
    courseGroupClientLanguages: state => state.mobConfig?.courseGroupClientLanguages || ['cs', 'en', 'de'],
    // authoritative for the private flow; courseLanguages is the fallback when unset,
    // with neither configured the list is empty (the view shows an error alert)
    coursePrivateClientLanguages: state => state.mobConfig?.coursePrivateClientLanguages || state.mobConfig?.courseLanguages || [],

    // Course Settings
    courseGroupIsWholeDayRequired: state => state.mobConfig?.courseGroupIsWholeDayRequired || false,

    // Rental Settings
    rentSelectSizeAutomatic: state => state.mobConfig?.rentSelectSizeAutomatic ?? true,
    shoeSizeItems: state => state.mobConfig?.shoeSizeItems || [],
    rentDisabledPointSells: state => state.mobConfig?.rentDisabledPointSells || [],
    rentDatePickTryOutEnabled: state => state.mobConfig?.rentDatePickTryOutEnabled ?? true,
    deliveryPoints: state => state.mobConfig?.deliveryPoints || [],

    // UI Display Settings
    rentProductCardImgHeightXs: state => state.mobConfig?.rentProductCardImgHeightXs ?? 250,
    rentProductCardImgHeightSm: state => state.mobConfig?.rentProductCardImgHeightSm || null,
    rentProductCardImgHeightMd: state => state.mobConfig?.rentProductCardImgHeightMd ?? 300,
    rentProductCardImgHeightLg: state => state.mobConfig?.rentProductCardImgHeightLg ?? 430,
    rentProductCardImgHeightXl: state => state.mobConfig?.rentProductCardImgHeightXl ?? 650,
    rentSearchSmCols: state => state.mobConfig?.rentSearchSmCols ?? 6,
    rentSearchMdCols: state => state.mobConfig?.rentSearchMdCols ?? 3,
    rentSearchLgCols: state => state.mobConfig?.rentSearchLgCols ?? 3,
    rentProductCardImgAspectRatio: state => state.mobConfig?.rentProductCardImgAspectRatio ?? (2/3),

    // Feature Flags
    useV2SchoolAvailability: state => state.mobConfig?.useV2SchoolAvailability || false,
    autoForwardSingleLocation: state => state.mobConfig?.autoForwardSingleLocation || false,
  },

  mutations: {
    doSetMobConfig(state, payload) {
      state.mobConfig = payload;
      // MOB-060 — LocaleUtil resolves the tenant default locale itself instead of taking
      // it as a third argument no caller passed. It is not a Vuex consumer, so the value
      // is pushed to it here, in step with the `defaultLocale` getter above.
      LocaleUtil.setDefaultLocale(payload?.defaultLocale);
    },

    doSetPointSellOpenHoursDefault(state, payload) {
      state.pointSellOpenHoursDefault = Array.isArray(payload) ? payload : [];
      state.pointSellOpenHoursDefaultStatus = OPEN_HOURS_READ.LOADED;
    },
    // MOB-094 — the schedules stay as they are: a failed read is never "no default schedule".
    doSetPointSellOpenHoursDefaultFailed(state) {
      state.pointSellOpenHoursDefaultStatus = OPEN_HOURS_READ.FAILED;
    },

    doSetPointSellOpenHoursExceptions(state, { key, schedules }) {
      state.pointSellOpenHoursExceptions[key] = schedules;
      delete state.pointSellOpenHoursExceptionsFailed[key]; // MOB-440: a success clears the mark
    },
    // MOB-440 — the schedules stay as they are: a failed read is never "no exceptions".
    doSetPointSellOpenHoursExceptionsFailed(state, { key }) {
      state.pointSellOpenHoursExceptionsFailed[key] = true;
    },

    doSetLastVisitedSegment(state, payload) {
      state.lastVisitedSegment = payload;
    },

    doSetRemovingItemId(state, payload) {
      state.removingItemId = payload;
    },

    doLocaleChange(state, payload) {
      // A code outside APP_LOCALES (a tenant `defaultLocale` such as `ru` or `cs_CZ`) changes
      // nothing, like an empty payload: the store language code and the 3 sinks below stay in
      // step. moment ignores an unknown code and Vuetify falls back to `en`.
      if (!APP_LOCALES.includes(payload)) return;
      console.log(state.locale);
      state.locale = payload;
      console.log(state.locale);
      // MOB-401 — Vuetify renders its own text (date picker month and weekday names,
      // `$vuetify.*` strings) in its own locale, which is never derived from vue-i18n.
      // It is pushed here, in step with the store language code, like LocaleUtil (MOB-060).
      vuetify.locale.current.value = payload;
      // MOB-399 — the document language drives the speech voice of assistive technology.
      // `index.html` keeps `lang="cs"` only until the first language is applied.
      if (typeof document !== "undefined") {
        document.documentElement.lang = payload;
      }
      // MOB-398 — moment's global locale ended at the last imported locale file (`hu`) and one
      // component set it. It is pushed here too, so it stays in step with the store language code.
      moment.locale(payload);
    },

    doSetLocations(state, payload) {
      if (payload != null) {
        state.locations = payload.sort((a, b) => {
          return a.orderNumber - b.orderNumber;
        });
      } else {
        state.locations = payload;
      }
      // Loaded-vs-loading signal for the search toolbars: an empty list before this
      // commit means "still loading", after it means "the tenant has no offer".
      state.locationsLoaded = true;
    },

    doSetLocation(state, payload) {
      state.location = payload;
      if (payload != null) {
        console.log("Ukladam locatoin do COOKIE:");
        console.log(payload);
        cookies.set("location", payload);
        console.log("kontrolni vypis:");
        console.log(cookies.get("location"));
      } else {
        cookies.remove("location");
      }
    },

    doSetPointSells(state, payload) {
      if (payload != null) {
        state.pointSells = payload.sort((a, b) => {
          return a.orderNumber - b.orderNumber;
        });
      } else {
        state.pointSells = payload;
      }
      // Same loaded signal as doSetLocations, for the rent toolbar.
      state.pointSellsLoaded = true;
    },

    doSetContactPointSells(state, payload) {
      state.contactPointSells = payload || [];
    },

    doSetPointMeetings(state, payload) {
      if (payload != null) {
        state.pointMeetings = payload.sort((a, b) => {
          return a.orderNumber - b.orderNumber;
        });
      } else {
        state.pointMeetings = payload;
      }
    },

    doSetSkillLevelClients(state, payload) {
      if (payload != null) {
        state.skillLevelClients = payload.sort((a, b) => {
          return (a.orderNumber || 0) - (b.orderNumber || 0);
        });
      } else {
        state.skillLevelClients = payload;
      }
    },

    doSetPointSell(state, payload) {
      state.pointSell = payload;
      if (payload != null) {
        cookies.set("pointSell", payload);
      } else {
        cookies.remove("pointSell");
      }
    },

    doSetCheckInDateSchool(state, payload) {
      payload = normalizeDatePayload(payload);
      state.checkInDateSchool = payload;
      if (payload != null) {
        cookies.set("checkInDateSchool", payload);
      } else {
        cookies.remove("checkInDateSchool");
      }
    },

    doSetCheckInDateMinRent(state, payload) {
      state.checkInDateMinRent = payload;
    },

    doSetCheckInDateRent(state, payload) {
      payload = normalizeDatePayload(payload);
      state.checkInDateRent = payload;
      if (payload != null) {
        cookies.set("checkInDateRent", payload);
      } else {
        cookies.remove("checkInDateRent");
      }
    },

    doSetCheckOutDateRent(state, payload) {
      payload = normalizeDatePayload(payload);
      state.checkOutDateRent = payload;
      if (payload != null) {
        cookies.set("checkOutDateRent", payload);
      } else {
        cookies.remove("checkOutDateRent");
      }
    },

    doSetRentCategories(state, payload) {
      if (payload != null) {
        state.rentCategories = payload.sort((a, b) => {
          return a.orderNumber - b.orderNumber;
        });
      } else {
        state.rentCategories = payload;
      }
    },

    doSetSchoolCategories(state, payload) {
      state.schoolCategories = payload;
    },

    doSetOrderr(state, orderr) {
      state.currentOrderr = orderr;
      if (orderr == null) {
        state.expireAt = null;
        cookies.remove("orderrId");
      } else {
        state.expireAt = orderr.expireAt;
        cookies.set("orderrId", orderr.id);
      }
    },

    doSetCurrentOrderrExpired(state, isExpired) {
      state.currentOrderrExpired = isExpired;
    },

    doSetCurrentOrderrPointSellDisabled(state, isDisabled) {
      state.currentOrderrPointSellDisabled = isDisabled;
    },

    doSetCurrentPayment(state, payment) {
      state.currentPayment = payment;
    },

    doSetStaticContentList(state, staticContentList) {
      state.staticContentList = staticContentList;
    },

    doSetRentProductRequirements(state, rentProductRequirements) {
      state.rentProductRequirements = rentProductRequirements;
    },
  },

  actions: {
    async setLastVisitedSegment(context, segment) {
      context.commit("doSetLastVisitedSegment", segment);
    },


    async localeChange(context, newLocale) {
      context.commit("doLocaleChange", newLocale);
    },

    async loadMobConfig(context) {
      console.log("loadMobConfig -- start:");
      console.log("mobDeploymentId:", context.state.mobDeploymentId);
      let mobConfig = null;
      try {
        console.log("loadMobConfig -- volani:");
        mobConfig = await APIAdapter.getLatestMOBConfig(
          context.state.appInstance,
          context.state.mobDeploymentId
        );
      } catch (e) {
        console.log("loadMobConfig -- ERROR");
        console.log(e);
      }

      console.log("loadMobConfig:");
      console.log(mobConfig);

      if (mobConfig == null) {
        console.error("loadMobConfig -- mobConfig is null, aborting");
        return;
      }

      context.commit("doSetMobConfig", mobConfig);

      // MOB-094: a failed read is recorded as FAILED, and the next demand reads again.
      try {
        await context.dispatch("ensureDefaultOpenHours");
      } catch (e) {
        console.error("loadMobConfig -- failed to load default open hours", e);
      }

      // The rental offset is HOURS and its field name says so (MOB-050); the three school
      // offsets are minutes. Read it only through BookingOffsetUtil, which also accepts the
      // pre-rename `rentBookingOffset` spelling.
      // The floor belongs to the chosen branch (MOB-227). A branch restored ahead of the
      // config is covered here; every later choice goes through the doSetPointSell
      // subscription at the bottom of this file.
      await context.dispatch("refreshRentBookingFloor");
      const rentMinDate = context.state.checkInDateMinRent;

      console.log("rentMinDate: " + rentMinDate);

      context.commit("doSetCheckInDateRent", rentMinDate);
      context.commit("doSetCheckOutDateRent", rentMinDate);
    },

    // MOB-227 — the rental booking floor of the CHOSEN branch: now + offset, moved past
    // every moment that branch is closed (default schedule, exception schedules, fully
    // closed days). The client chooses the branch before any rental date shows, so no
    // floor for "no branch" exists — until a branch is set, the fixed legacy hours apply.
    // The default schedule is already in state, so that floor is committed at once; the
    // exception schedules arrive asynchronously and correct it.
    async refreshRentBookingFloor(context) {
      const pointSell = context.state.pointSell;
      const pointSellMssId = pointSell != null ? pointSell.mssId : null;

      // MOB-094: the floor of a branch needs its default schedule. When the read has not
      // succeeded yet, this refresh is a demand: it reads again (or joins the read in flight).
      // A failed read leaves the default unknown; the pages block the booking on that state.
      if (
        pointSellMssId != null &&
        context.state.mobConfig != null &&
        context.state.pointSellOpenHoursDefaultStatus !== OPEN_HOURS_READ.LOADED
      ) {
        try {
          await context.dispatch("ensureDefaultOpenHours");
        } catch (e) {
          console.error("refreshRentBookingFloor -- failed to load default open hours", e);
        }
        // The client chose another branch while the hours loaded: that refresh owns the floor.
        const current = context.state.pointSell;
        if (current == null || current.mssId !== pointSellMssId) return;
      }

      const offsetHours = BookingOffsetUtil.rentOffsetHours(context.state.mobConfig);
      const defaultSchedule =
        pointSellMssId != null
          ? context.getters.defaultOpenHoursByPointSell(pointSellMssId)
          : null;

      const commitFloor = (exceptionSchedules) => {
        context.commit(
          "doSetCheckInDateMinRent",
          DateUtil.bookingMinDate(offsetHours, defaultSchedule, exceptionSchedules)
        );
      };

      commitFloor([]);

      if (pointSellMssId != null && context.state.mobConfig != null) {
        let exceptionSchedules = null;
        try {
          exceptionSchedules = await context.dispatch("loadOpenHoursExceptions", {
            pointSellMssId,
            ...DateUtil.rentOpenHoursWindow(),
          });
        } catch (e) {
          console.error("refreshRentBookingFloor -- failed to load exception open hours", e);
        }

        // The client chose another branch while this one loaded: that refresh owns the floor.
        const current = context.state.pointSell;
        if (current == null || current.mssId !== pointSellMssId) return;

        if (exceptionSchedules != null) commitFloor(exceptionSchedules);
      }

      // A pickup or return day below the floor is a day no picker offers: raise it.
      const floor = context.state.checkInDateMinRent;
      if (context.state.checkInDateRent != null && context.state.checkInDateRent < floor) {
        context.commit("doSetCheckInDateRent", floor);
      }
      if (context.state.checkOutDateRent != null && context.state.checkOutDateRent < floor) {
        context.commit("doSetCheckOutDateRent", floor);
      }
    },

    // MOB-094 — the only reader of the default opening hours of all branches. It reads only
    // while the status is not LOADED. Parallel callers share 1 request. A success is committed
    // (status LOADED); a failure sets FAILED and rejects, and it is not cached: the next demand
    // reads again. The status stays FAILED during that read, until a read succeeds. No cap and
    // no timer: every read needs a demand (the boot, a branch choice, a booking page, the
    // checkout, a retry button).
    async ensureDefaultOpenHours(context) {
      if (context.state.pointSellOpenHoursDefaultStatus === OPEN_HOURS_READ.LOADED) {
        return context.state.pointSellOpenHoursDefault;
      }
      if (defaultOpenHoursRequest != null) return defaultOpenHoursRequest;
      if (context.state.mobConfig == null) return null; // nothing to read against yet

      const request = (async () => {
        try {
          const schedules = await APIAdapter.listDefaultOpenHours(
            context.state.appInstance,
            context.state.mobConfig.mobVersion
          );
          context.commit("doSetPointSellOpenHoursDefault", schedules);
          return schedules;
        } catch (e) {
          context.commit("doSetPointSellOpenHoursDefaultFailed");
          throw e;
        } finally {
          defaultOpenHoursRequest = null;
        }
      })();
      defaultOpenHoursRequest = request;
      return request;
    },

    // The dated exception schedules of 1 branch in 1 window, cached by key. MOB-440: only a
    // success is cached. A failure marks the key, caches nothing and rejects. Parallel callers
    // share 1 request.
    async loadOpenHoursExceptions(context, { pointSellMssId, windowFrom, windowTo }) {
      if (pointSellMssId == null || !windowFrom || !windowTo) return [];
      const key = openHoursExceptionsKey(pointSellMssId, windowFrom, windowTo);
      const cached = context.state.pointSellOpenHoursExceptions[key];
      if (cached != null) return cached;

      // Two callers ask for the same window at the same moment (the doSetPointSell
      // subscription and the booking wrapper, MOB-227): they share one request.
      const inFlight = openHoursExceptionRequests.get(key);
      if (inFlight != null) return inFlight;

      const request = (async () => {
        try {
          const schedules = await APIAdapter.listExceptionOpenHours(
            context.state.appInstance,
            context.state.mobConfig.mobVersion,
            pointSellMssId,
            windowFrom,
            windowTo
          );
          context.commit("doSetPointSellOpenHoursExceptions", { key, schedules });
          return schedules;
        } catch (e) {
          // MOB-440: a failed read is marked, never cached as "no exceptions". The next demand
          // reads again (no cap, no timer), as for the default hours (MOB-094).
          context.commit("doSetPointSellOpenHoursExceptionsFailed", { key });
          throw e;
        } finally {
          openHoursExceptionRequests.delete(key);
        }
      })();
      openHoursExceptionRequests.set(key, request);
      return request;
    },

    async loadLocations(context) {
      let locations = await APIAdapter.listLocations(
        context.state.appInstance,
        context.state.mobConfig.mobVersion
      );
      console.log("loadLocations:");
      console.log(locations);

      context.commit("doSetLocations", locations);

      if (
        context.state.location == null &&
        context.state.locations != null &&
        context.state.locations.length > 0
      ) {
        context.commit("doSetLocation", context.state.locations[0]);
      }
    },

    async loadPointSells(context) {
      let pointSells = await APIAdapter.listPointSell(
        context.state.appInstance,
        context.state.mobConfig.mobVersion
      );
      console.log("loadPointSells:");
      console.log(pointSells);

      context.commit("doSetContactPointSells", pointSells);

      // vyfiltrovat nechtenne (MOB-043: jedina definice pravidla — PointSellUtil)
      pointSells = pointSells.filter((ps) =>
        isPointSellRentable(ps, context.getters.rentDisabledPointSells)
      );

      context.commit("doSetPointSells", pointSells);

      //console.log(context.state.pointSell == null);

      if (
        context.state.pointSell == null &&
        context.state.pointSells != null &&
        context.state.pointSells.length > 0
      ) {
        context.commit("doSetPointSell", context.state.pointSells[0]);
      }
    },

    async loadPointMeetings(context) {
      let pointMeetings = await APIAdapter.listPointMeeting(
        context.state.appInstance,
        context.state.mobConfig.mobVersion
      );
      console.log("loadPointMeetings:");
      console.log(pointMeetings);

      context.commit("doSetPointMeetings", pointMeetings);
    },

    async loadSkillLevelClients(context) {
      if (skillLevelClientsRequest != null) return skillLevelClientsRequest;

      const request = (async () => {
        try {
          let skillLevelClients = await APIAdapter.listSkillLevelClients(
            context.state.appInstance,
            context.state.mobConfig.mobVersion
          );
          console.log("loadSkillLevelClients:");
          console.log(skillLevelClients);

          context.commit("doSetSkillLevelClients", skillLevelClients);
        } finally {
          skillLevelClientsRequest = null;
        }
      })();
      skillLevelClientsRequest = request;
      return request;
    },

    async loadListCategoriesAndProductsRent(context) {
      let result = await APIAdapter.loadListCategoriesAndProductsByProductType(
        context.state.appInstance,
        context.state.mobConfig.mobVersion,
        "RENTAL"
      );
      context.commit("doSetRentCategories", result);
    },

    async loadListCategoriesAndProductsSchool(context) {
      let result = await APIAdapter.loadListCategoriesAndProductsByProductType(
        context.state.appInstance,
        context.state.mobConfig.mobVersion,
        "SCHOOL"
      );
      console.log("loadListCategoriesAndProductsSchool");
      console.log(result);
      context.commit("doSetSchoolCategories", result);
    },

    // vytvoreni prazdne 0objednavky
    // napojeno na backend pres Mutation
    async createOrderr(context) {
      try {
        const createOrderrDraftIn = {
          appInstance: context.state.appInstance,
          groups: context.state.appInstance,
          mobVersion: context.state.mobConfig.mobVersion,
          // MOB-516: the binding stays through the fan-out of wave 2, null-safe —
          // `state.pointSell` is null before `loadPointSells`. The layer of wave 2 ignores
          // it, its removal is MOB-232.
          orderrPointSellId:
            context.state.pointSell != null ? context.state.pointSell.id : null,
          language: LocaleUtil.transformLocaleToLanguage(context.state.locale),
        };

        let orderrId = await APIAdapter.createOrderrDraft(createOrderrDraftIn);

        // MOB-040: a draft that failed must not be read back. This used to pass the
        // adapter's null straight into `getOrderr`, which put `GetOrderr(id: null)` on the
        // wire — `getOrderr($id: ID!)` cannot answer it — and the TypeError that followed
        // on the null order was swallowed by the catch below, so nothing downstream ever
        // learned that the order does not exist.
        if (orderrId == null) {
          throw new Error("createOrderr: objednavku se nepodarilo zalozit");
        }

        console.log("objednavka zalozena");

        const orderr = await context.dispatch("getOrderr", orderrId);

        if (orderr == null) {
          throw new Error("createOrderr: zalozenou objednavku se nepodarilo nacist");
        }

        // MOB-100: the draft was created by this request, so its `createdAt` is the backend
        // clock at that instant — the one server "now" the client ever receives. Measuring
        // the device clock against it here is what keeps the cart countdown (and the expiry
        // check below) off a skewed device clock.
        ServerClock.anchor(orderr && orderr.createdAt);

        return orderr;
      } catch (error) {
        // MOB-040: logged and rethrown. Swallowing here left `currentOrderr` null with no
        // caller aware of it, and the next dereference of it was the only sign the client
        // ever got.
        console.error("Nepodarilo se zalozit objednavlku", error);
        throw error;
      }
    },

    // vola se jako akce pro zahajeni pridani polozky do kosiku
    async addToCart(context, orderrItemGroup) {
      console.log("store action addToCart");
      console.log(orderrItemGroup);

      //await context.dispatch("loadCurrentOrderrDRAFT");

      /// create orderr, jestlize neexistuje
      if (context.state.currentOrderr == null) {
        await context.dispatch("createOrderr");
        console.log("store action addToCart -- objednavka zalozena");
      }

      // MOB-040: without an order there is nothing to hang the item group on. Building the
      // input anyway dereferenced `currentOrderr.id` and threw a TypeError that the view's
      // error handler could not read; rejecting here is what makes the handler show its
      // translated failure message instead.
      if (context.state.currentOrderr == null) {
        throw new Error("addToCart: objednavka neexistuje, polozku nelze pridat do kosiku");
      }

      console.log("store action addToCart -- vytvareni subject profiles");
      console.log(orderrItemGroup.subjectProfiles);

      let subjectProfileIns = [];
      if (
        orderrItemGroup.subjectProfiles != null &&
        orderrItemGroup.subjectProfiles != undefined &&
        orderrItemGroup.subjectProfiles.length > 0 &&
        orderrItemGroup.subjectProfiles[0] != null
      ) {
        for (let i = 0; i < orderrItemGroup.subjectProfiles.length; i++) {
          let subjectProfileIn = {
            name: orderrItemGroup.subjectProfiles[i].name,
            surname: orderrItemGroup.subjectProfiles[i].surname,
            weight: orderrItemGroup.subjectProfiles[i].weight,
            height: orderrItemGroup.subjectProfiles[i].height,
            shoeSize: orderrItemGroup.subjectProfiles[i].shoeSize,
            age: orderrItemGroup.subjectProfiles[i].age,
            birthDate: orderrItemGroup.subjectProfiles[i].birthDate,
            gender: orderrItemGroup.subjectProfiles[i].gender,
            rentAbilityLevel: orderrItemGroup.subjectProfiles[i].rentAbilityLevel,
            schoolSkillLevelClientId: orderrItemGroup.subjectProfiles[i].schoolSkillLevelClient,
            note: orderrItemGroup.subjectProfiles[i].note,
          };
          if(orderrItemGroup.subjectProfiles[i].language != undefined && orderrItemGroup.subjectProfiles[i].language != null) {
            subjectProfileIn.language = orderrItemGroup.subjectProfiles[i].language;
          }
          if(orderrItemGroup.subjectProfiles[i].customFormData != undefined && orderrItemGroup.subjectProfiles[i].customFormData != null) {
            subjectProfileIn.customFormData = JSON.stringify(orderrItemGroup.subjectProfiles[i].customFormData);
          }
          subjectProfileIns.push(subjectProfileIn);
        }
      }

      console.log("store action addToCart -- vytvareni subject profiles - done");

      console.log("store action addToCart -- vytvareni orderrItemGroupIn");

      let orderrItemGroupIn = {
        groups: context.state.appInstance,
        appInstance: context.state.appInstance,
        mobVersion: context.state.mobConfig.mobVersion,
        subjectProfiles: subjectProfileIns,
        description: orderrItemGroup.description,
        itemsCount: orderrItemGroup.itemsCount,
        numberOfPersons: orderrItemGroup.numberOfPersons,
        length: orderrItemGroup.length,
        priceListType: orderrItemGroup.priceListType,
        // Wall-clock times at the PointSell, in the one form of the API (MOB-058). An
        // unreadable value throws here, so the call below does not start.
        dateStart: WallClock.toWire(orderrItemGroup.dateStart),
        dateEnd: WallClock.toWire(orderrItemGroup.dateEnd),
        orderrItemGroupOrderrId: context.state.currentOrderr.id,
        orderrItemGroupProductId: orderrItemGroup.product.id,
        orderrItemGroupLocationId: orderrItemGroup.location.id,
      };

      // MOB-143: a group with no meeting point (a RENTAL group) sends no key. An explicit
      // null is refused by DynamoDB, because the key is an index key, and an older backend
      // does not remove it.
      if (orderrItemGroup.pointMeeting != null) {
        orderrItemGroupIn.orderrItemGroupPointMeetingId = orderrItemGroup.pointMeeting.id;
      }

      if (orderrItemGroup.set != null) {
        orderrItemGroupIn.orderrItemGroupSetId = orderrItemGroup.set.id;
      }

      if (orderrItemGroup.productPacket != null) {
        orderrItemGroupIn.orderrItemGroupProductPacketId = orderrItemGroup.productPacket.id;
      }

      let orderrItemsIn = [];

      for (let i = 0; i < orderrItemGroup.orderrItems.length; i++) {
        let oi = orderrItemGroup.orderrItems[i];

        let oiInput = {
          groups: context.state.appInstance,
          appInstance: context.state.appInstance,
          mobVersion: context.state.mobConfig.mobVersion,
          productId: oi.product.id,
          article:
            oi.article != undefined && oi.article != null ? oi.article : null,
          groupCourse: TransformUtil.transformGroupCourseToGroupCourseIn(
            oi.groupCourse
          ),
          groupCourseDateStart: oi.groupCourseDateStart != undefined && oi.groupCourseDateStart != null ? oi.groupCourseDateStart.format("YYYY-MM-DD") : null,
        };
        if(oi.groupCourseLessons != undefined) {
          oiInput.groupCourseLessons = TransformUtil.transformGroupCourseLessonsToIn(oi.groupCourseLessons);
        } else {
          oiInput.groupCourseLessons = null;
        }
        if (oi.lessons != undefined) {
          oiInput.lessons = oi.lessons;
        } else {
          oiInput.lessons = null;
        }

        if (oi.language != undefined) {
          oiInput.language = oi.language;
        } else {
          oiInput.language = null;
        }

        if (oi.languages != undefined) {
          oiInput.languages = oi.languages;
        } else {
          oiInput.languages = null;
        }

        if (oi.orderrItemSkillLevelClientId != undefined && oi.orderrItemSkillLevelClientId != null) {
          oiInput.orderrItemSkillLevelClientId = oi.orderrItemSkillLevelClientId;
        }
        // Note: Don't set to null - omit field entirely for sparse GSI index

        if (oi.sameInstructorRequired != undefined) {
          oiInput.sameInstructorRequired = oi.sameInstructorRequired;
        } else {
          oiInput.sameInstructorRequired = null;
        }

        if (oi.numberOfHoursRequired != undefined) {
          oiInput.numberOfHoursRequired = oi.numberOfHoursRequired;
        } else {
          oiInput.numberOfHoursRequired = null;
        }

        orderrItemsIn.push(oiInput);
      }

      console.log("store action bookOrderrItemGroup -- zahajeno ukladani");

      let addToCartIn = {
        orderrItemGroup: orderrItemGroupIn,
        orderrItems: orderrItemsIn,
      };

      console.log("addToCartIn:");
      console.log(addToCartIn);

      await APIAdapter.addOrderrItemGroupToCart(addToCartIn);

      console.log("store action bookOrderrItemGroup -- ulozeno.");

      context.dispatch("getOrderr", context.state.currentOrderr.id);
    },

    async removeFromCart(context, orderrItemGroup) {
      console.log("removeFromCart -- start");
      context.commit("doSetRemovingItemId", orderrItemGroup.id);

      try {
        await APIAdapter.removeOrderrItemGroupFromCart(orderrItemGroup.id);
        console.log("removeFromCart -- hotovo");
        await context.dispatch("getOrderr", context.state.currentOrderr.id);
      } finally {
        context.commit("doSetRemovingItemId", null);
      }
    },

    async clearCurrentOrderr(context) {
      context.commit("doSetOrderr", null);
    },

    async getOrderr(context, orderrId) {
      // MOB-040: `getOrderr($id: ID!)` is non-null in the schema, so a falsy id is a request
      // the backend cannot answer — noise that hides the failure that produced the falsy id
      // in the first place. It never goes on the wire.
      if (orderrId == null) {
        console.error("getOrderr: volano bez id objednavky, dotaz se neodesila");
        return null;
      }

      let orderr = await APIAdapter.getOrderr(orderrId);

      // MOB-040: the adapter answers an unreadable order with null. Reading `bookingState`
      // off it threw a TypeError deep inside this action, which the callers then reported
      // as if the app were broken. Leave the state as it was and let the caller decide.
      if (orderr == null) {
        console.error("getOrderr: objednavku se nepodarilo nacist", orderrId);
        return null;
      }

      if (
        orderr.bookingState != "DRAFT" &&
        // MOB-100: `expireAt` is server-issued, so it is compared against the server's now.
        moment(orderr.expireAt).toDate() < ServerClock.nowDate()
      ) {
        context.commit("doSetCurrentOrderrExpired", true);
      } else {
        context.commit("doSetCurrentOrderrExpired", false);
      }

      context.commit("doSetOrderr", orderr);

      // Synchronize pointSell from the order to the state, when the order holds a RENTAL
      // item group (MOB-516, below). The dropdown then shows the PointSell the rental is locked to.
      // MOB-065: state.pointSell is dereferenced for `location` and `pointMeeting`
      // (RentBooking builds its item group from them, RentSearch filters on
      // location.id), so a branch object that carries neither must never replace a
      // loaded one. Fill the missing connections from the loaded branch list, or from
      // the branch already in state, before committing.
      // MOB-043: the order can be bound to a branch that has since been put into
      // `mobConfig.rentDisabledPointSells`. That branch is filtered out of every list the
      // client can pick from, so making it the active selection puts a value in the rental
      // branch dropdown that is not among its own options and lets the rental flow keep
      // booking against a branch that was taken off online sale. `state.pointSell` is read
      // by the rental UI only, so refusing the sync costs a non-rental order nothing.
      const orderrPointSellDisabled =
        orderr != null &&
        orderr.pointSell != null &&
        isPointSellRentDisabled(
          orderr.pointSell,
          context.getters.rentDisabledPointSells
        );

      // Refusing the sync alone would leave `state.pointSell` (branch A) disagreeing with
      // `currentOrderr.pointSell` (branch B) — the very state RentSearchToolbar refuses to
      // let a client create while the cart has items, and one that would write branch A's
      // location into an order booked at branch B. So the cart raises
      // `currentOrderrPointSellDisabled`; App.vue turns that into a modal dialog whose only
      // action drops the order and returns the client to the branch choice.
      //
      // That dialog is destructive, so it is raised only for a cart the flag actually
      // concerns:
      //
      //  - the cart must hold a RENTAL item group. `createOrderr` binds every order to
      //    `state.pointSell.id`, ski-school orders included, and
      //    `rentDisabledPointSells` disables ONLINE RENTAL at that branch — nothing else.
      //    A school-only cart is left completely alone (MOB-043 "Not in scope": the flag is
      //    not to be widened beyond rental).
      //  - the order must still be a DRAFT. The dialog exists to stop a cart the client is
      //    still filling; an order past DRAFT is on its way through checkout or already
      //    booked, and must stay viewable with the branch it was booked at, because
      //    CheckoutPickUp and OrderrSummary read `orderr.pointSell` directly. (`addToCart`
      //    reuses whatever order is in state, so a non-DRAFT order is not by itself proof
      //    that nothing more can be added — but discarding an order the client has already
      //    paid for or checked out is the worse of the two outcomes.)
      //
      // The order object itself is never modified here. MOB-043 — the rental flag concerns a
      // cart that rents something; a school-only cart is left alone (`PointSellLock`).
      context.commit(
        "doSetCurrentOrderrPointSellDisabled",
        orderrPointSellDisabled &&
          orderr.bookingState == "DRAFT" &&
          PointSellLock.hasRentalItemGroup(orderr)
      );

      // MOB-516: the header PointSell locks the RENTAL part only. A school-only or an empty
      // orderr carries a header too (every orderr written before wave 2 does), but nothing in
      // it is booked at that PointSell, so the restore leaves the customer's selection alone.
      // `rentalPointSellLock` is null in those cases, and when the header is null.
      const rentalLock = PointSellLock.rentalPointSellLock(orderr);
      if (rentalLock != null) {
        if (!orderrPointSellDisabled) {
          context.commit(
            "doSetPointSell",
            completePointSell(rentalLock, context.state)
          );
        } else {
          console.warn(
            "getOrderr: pobocka objednavky je vyrazena z online pujcovny (rentDisabledPointSells), " +
              "nebude nastavena jako aktivni: mssId=" + rentalLock.mssId
          );
        }
      }

      return orderr;
    },

    async checkoutOrderr(context, payload) {
      console.log("checkoutOrderr -- start");

      let createSubjectIn = {
        ...payload.contactData,
        mobVersion: context.state.mobConfig.mobVersion,
        appInstance: context.state.appInstance,
        groups: context.state.appInstance,
        cognitoId: null,
        documentType: "ID",
      };
      console.log(createSubjectIn);

      let orderrId = payload.orderrId;
      console.log(orderrId);

      await APIAdapter.checkoutOrderr(orderrId, createSubjectIn);

      console.log("checkoutOrderr -- end");
    },

    async getPayment(context, paymentId) {
      console.log("getPayment -- start");
      let payment = await APIAdapter.getPayment(paymentId);
      context.commit("doSetCurrentPayment", payment);
      console.log(payment);
      console.log("getPayment -- end");
    },

    async updateCurrentOrderrExpired(context, isExpired) {
      context.commit("doSetCurrentOrderrExpired", isExpired);
    },

    async expireOrderr(context) {
      context.commit("doSetOrderr", null);
      context.commit("doSetCurrentOrderrExpired", false);
      context.commit("doSetCurrentOrderrPointSellDisabled", false);
    },

    // MOB-043: the client acknowledged that the branch their cart is bound to is no longer
    // bookable online. The DRAFT is dropped locally (the `orderrId` cookie goes with it, so
    // the next load does not restore it) and expires on the backend on its own schedule,
    // exactly like an abandoned cart. Nothing can be added to it or checked out afterwards.
    async discardOrderrWithDisabledPointSell(context) {
      context.commit("doSetOrderr", null);
      context.commit("doSetCurrentOrderrPointSellDisabled", false);
      context.commit("doSetCurrentOrderrExpired", false);
    },

    async loadStaticContentList(context) {
      console.log("loadStaticContentList -- start");
      let staticContentList = await APIAdapter.listStaticContent(
        context.state.appInstance,
        context.state.mobConfig.mobVersion
      );
      context.commit("doSetStaticContentList", staticContentList);
      console.log(staticContentList);
      console.log("loadStaticContentList -- end");
    },

    async loadRentProductRequirements(context) {
      console.log("loadRentProductRequirements -- start");
      let rentProductRequirements =
        await APIAdapter.listRentProductRequirements(
          context.state.appInstance,
          context.state.mobConfig.mobVersion
        );
      context.commit("doSetRentProductRequirements", rentProductRequirements);
      console.log(rentProductRequirements);
      console.log("loadRentProductRequirements -- end");
    },
  },

  modules: {},
});

// MOB-227: eight call sites choose a branch (views, the router, getOrderr). One
// subscription keeps the floor with the branch, so no call site can forget it.
store.subscribe((mutation) => {
  if (mutation.type === "doSetPointSell") {
    store.dispatch("refreshRentBookingFloor");
  }
});

export default store;
